import { Star } from "lucide-react";
import { cn } from "@/lib/utils";

import { GOOGLE_BUSINESS_URL as GOOGLE_MAPS_URL } from "@/data/googleReviews";

interface GoogleRatingBadgeProps {
  variant?: "default" | "compact" | "inline";
  className?: string;
}

export default function GoogleRatingBadge({ variant = "default", className }: GoogleRatingBadgeProps) {
  const stars = [...Array(5)].map((_, i) => (
    <Star key={i} className="h-3.5 w-3.5 fill-gold text-gold" />
  ));

  const linkProps = {
    href: GOOGLE_MAPS_URL,
    target: "_blank" as const,
    rel: "noopener noreferrer",
    "aria-label": "Voir notre fiche Google Maps",
    className: "hover:opacity-80 transition-opacity",
  };

  if (variant === "inline") {
    return (
      <a {...linkProps} className={cn("inline-flex items-center gap-1.5 hover:opacity-80 transition-opacity", className)}>
        <span className="flex gap-0.5">{stars}</span>
        <span className="text-xs text-muted-foreground font-medium">5/5</span>
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" aria-hidden="true">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
      </a>
    );
  }

  if (variant === "compact") {
    return (
      <a {...linkProps} className={cn("flex items-center gap-2 hover:opacity-80 transition-opacity w-fit", className)}>
        <div className="flex gap-0.5">{stars}</div>
        <span className="text-sm text-muted-foreground font-medium">5/5 sur Google</span>
      </a>
    );
  }

  return (
    <a {...linkProps} className={cn("flex items-center gap-3 px-4 py-2.5 rounded-full border border-gold/20 bg-gold/5 backdrop-blur-sm w-fit hover:opacity-80 transition-opacity", className)}>
      <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0" aria-hidden="true">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
      </svg>
      <div className="flex gap-0.5">{stars}</div>
      <span className="text-sm font-medium text-foreground/80">5/5 sur Google</span>
    </a>
  );
}
